import { useState, useEffect, useMemo } from 'react'

import { clsx } from 'clsx'
import isMatch from 'lodash/isMatch'
import matches from 'lodash/matches'

import {
  Button,
  ChannelIcon,
  type ChannelType,
  Flex,
  PlusIcon,
  ScrollArea,
  StatusDot,
  Text,
  Tooltip,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import { useSelectedProfiles } from '~publish/hooks/useSelectedProfiles'
import { useVisibleElements } from '~publish/hooks/useVisibleElements'

import ChannelItem from './ChannelItem'
import { ToggleChannels } from './ToggleChannels'
import { useFormattedProfiles } from '../../hooks/useProfiles'
import type { ComposerProfile } from '../../stores/types'

import styles from './ChannelSelection.module.css'

/**
 * Button component that shows the number of hidden channels and allows expanding the channel list
 *
 * @param hasHiddenChannelsSelected - Whether any of the hidden channels are currently selected
 * @param hiddenCount - Number of channels that are currently hidden
 * @param onExpand - Callback function triggered when the button is clicked to expand the channel list
 * @returns JSX element containing the expand channels button
 */
const ExpandChannelsButton = ({
  hasHiddenChannelsSelected,
  hiddenCount,
  onExpand,
}: {
  hasHiddenChannelsSelected: boolean
  hiddenCount: number
  onExpand: () => void
}): JSX.Element => {
  return (
    <span className={styles.remainingCountWrapper}>
      <Button
        variant="secondary"
        size="medium"
        className={clsx(
          styles.remainingCount,
          hasHiddenChannelsSelected && styles.selected,
        )}
        onClick={onExpand}
        aria-label="Show more channels"
      >
        <PlusIcon />
        {hiddenCount}
      </Button>
      {hasHiddenChannelsSelected && <StatusDot className={styles.statusDot} />}
    </span>
  )
}

/**
 * Renders a tooltip content showing a list of social media channels
 *
 * @param channels - Array of ComposerProfile objects representing social media channels
 * @returns JSX element containing the tooltip content, or null if no channels provided
 */
const ToolTipContent = ({
  channels,
}: {
  channels: ComposerProfile[]
}): JSX.Element | null => {
  // If there are no channels, don't show the tooltip
  if (channels.length === 0) {
    return null
  }

  return (
    <Flex direction="column" gap="2xs">
      {channels.map((channel) => (
        <Flex direction="row" align="center" gap="xs" key={channel?.id}>
          <ChannelIcon
            color="inverted"
            size="xsmall"
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            type={channel?.service.name as ChannelType}
          />
          <Text size="sm">{channel?.service.formattedUsername}</Text>
        </Flex>
      ))}
    </Flex>
  )
}

const ChannelSelection = (): JSX.Element => {
  const [isExpanded, setIsExpanded] = useState(true)

  const { formattedProfiles: profiles } = useFormattedProfiles()
  const { selectedProfiles } = useSelectedProfiles()

  const { containerRef, measurementRef, visibleCount, hiddenCount } =
    useVisibleElements({ minimumVisibleCount: 1 })

  const visibleChannels = isExpanded
    ? profiles
    : profiles?.slice(0, visibleCount)

  // Get hidden channels (those beyond visibleCount)
  const hiddenChannels = useMemo(
    () => profiles?.slice(visibleCount) ?? [],
    [profiles, visibleCount],
  )

  // Check if any hidden channels are selected
  const selectedProfilesIds = useMemo(
    () => new Set(selectedProfiles.map((profile) => profile.id)),
    [selectedProfiles],
  )

  const hiddenSelectedChannels = useMemo(
    () =>
      hiddenChannels.filter((channel) => selectedProfilesIds.has(channel.id)),
    [hiddenChannels, selectedProfilesIds],
  )

  const hasHiddenChannelsSelected = hiddenSelectedChannels.length > 0

  // Calculate selection constraints for each profile/channel
  // This is used to determine if a profile/channel requires a tooltip message to explain a selection issue
  // An example use case is when a user selects a Facebook Page and a Facebook Group,
  // we need to show a tooltip message to explain that they cannot schedule a post for both at the same time
  const selectionConstraints = useMemo(
    () => ({
      hasFacebookGroup: selectedProfiles.some(
        matches({ serviceName: 'facebook', serviceType: 'group' }),
      ),
      hasFacebookPage: selectedProfiles.some(
        matches({ serviceName: 'facebook', serviceType: 'page' }),
      ),
      hasInstagramProfile: selectedProfiles.some(
        matches({ serviceName: 'instagram', serviceType: 'profile' }),
      ),
      hasInstagramBusiness: selectedProfiles.some(
        matches({ serviceName: 'instagram', serviceType: 'business' }),
      ),
    }),
    [selectedProfiles],
  )

  // Calculate tooltip content and disabled states for each profile/channel
  const getTooltipContentForChannelItem = useMemo(() => {
    return (profile: ComposerProfile): string => {
      // Profile-specific checks
      const isInstagramProfile = isMatch(profile, {
        serviceName: 'instagram',
        serviceType: 'profile',
      })
      const isInstagramBusiness = isMatch(profile, {
        serviceName: 'instagram',
        serviceType: 'business',
      })
      const isFacebookGroup = isMatch(profile, {
        serviceName: 'facebook',
        serviceType: 'group',
      })
      const isFacebookPage = isMatch(profile, {
        serviceName: 'facebook',
        serviceType: 'page',
      })
      const isLocked = profile.isDisabled

      // Selection disabled states
      const isFacebookSelectionDisabled =
        (isFacebookGroup && selectionConstraints.hasFacebookPage) ||
        (isFacebookPage && selectionConstraints.hasFacebookGroup)

      const isInstagramSelectionDisabled =
        (isInstagramProfile && selectionConstraints.hasInstagramBusiness) ||
        (isInstagramBusiness && selectionConstraints.hasInstagramProfile)

      // Tooltip content calculation
      const formattedServiceType =
        profile.serviceType.charAt(0).toUpperCase() +
        profile.serviceType.slice(1)
      const locationSuffix =
        profile.locationData && profile.locationData.trim()
          ? ` (${profile.locationData.trim()})`
          : ''

      const profileTooltipBaseContents = profile.isFake
        ? `Preview ${formattedServiceType}${locationSuffix}`
        : `${profile.service.username} - ${formattedServiceType}${locationSuffix}`

      let tooltipContent = isLocked
        ? `${profileTooltipBaseContents} (${
            profile.disabledMessage || 'locked'
          })`
        : `${profileTooltipBaseContents}`

      if (isFacebookSelectionDisabled) {
        tooltipContent =
          'You cannot schedule a post for Facebook Pages and Facebook Groups at the same time.'
      }

      if (isInstagramSelectionDisabled) {
        tooltipContent =
          'You cannot schedule a post for Instagram Personal Account and Instagram Business at the same time.'
      }

      return tooltipContent
    }
  }, [selectionConstraints])

  const showHiddenChannelsButton =
    hiddenChannels && hiddenChannels.length > 0 && !isExpanded

  const handleExpand = (): void => {
    setIsExpanded(!isExpanded)
  }

  // Handle clicks to collapse when user interacts with composer text input
  useEffect(() => {
    const handleTextAreaClick = (event: MouseEvent): void => {
      if (event.target instanceof Element && isExpanded) {
        const composerTextArea = event.target.closest(
          '[data-testid="composer-text-area"]',
        )

        if (composerTextArea) {
          setIsExpanded(false)
        }
      }
    }

    // Add event listener when component is expanded
    if (isExpanded) {
      document.addEventListener('mousedown', handleTextAreaClick)
    }

    // Cleanup event listener
    return (): void => {
      document.removeEventListener('mousedown', handleTextAreaClick)
    }
  }, [isExpanded, containerRef])

  return (
    <>
      <VisuallyHidden>Select channels</VisuallyHidden>

      <ToggleChannels
        className={clsx(
          styles.channelSelection,
          isExpanded && hiddenCount > 0 && styles.expanded,
        )}
        ref={containerRef}
        value={Array.from(selectedProfilesIds)}
      >
        <Flex
          ref={measurementRef}
          className={clsx(
            styles.channelSelectionMeasurement,
            styles.selectionWrapper,
          )}
        >
          {profiles?.map((profile) => (
            <ChannelItem
              key={`measurement-${profile.id}`}
              profile={profile}
              isSelected={false}
              tooltipContent={''}
            />
          ))}
          <span className={styles.remainingCountWrapper}>
            <Button
              variant="secondary"
              size="medium"
              className={clsx(styles.remainingCount)}
            >
              <PlusIcon />
              10
            </Button>
          </span>
        </Flex>

        <ScrollArea
          key={
            isExpanded
              ? 'channel-selection-expanded'
              : 'channel-selection-collapsed'
          }
          gradientFade={!isExpanded ? undefined : 15}
        >
          <Flex
            wrap={isExpanded ? 'wrap' : 'nowrap'}
            align="center"
            style={{ height: '50px' }}
            className={styles.selectionWrapper}
          >
            {visibleChannels?.map((profile) => (
              <ChannelItem
                key={profile.id}
                profile={profile}
                isSelected={selectedProfilesIds.has(profile.id)}
                tooltipContent={getTooltipContentForChannelItem(profile)}
              />
            ))}
            {showHiddenChannelsButton &&
              (hiddenSelectedChannels.length > 0 ? (
                <Tooltip
                  align="start"
                  content={<ToolTipContent channels={hiddenSelectedChannels} />}
                >
                  <Flex className={styles.tooltipWrapper}>
                    <ExpandChannelsButton
                      hasHiddenChannelsSelected={hasHiddenChannelsSelected}
                      hiddenCount={hiddenCount}
                      onExpand={handleExpand}
                    />
                  </Flex>
                </Tooltip>
              ) : (
                <ExpandChannelsButton
                  hasHiddenChannelsSelected={hasHiddenChannelsSelected}
                  hiddenCount={hiddenCount}
                  onExpand={handleExpand}
                />
              ))}
          </Flex>
        </ScrollArea>
      </ToggleChannels>
    </>
  )
}

export default ChannelSelection
